import React, { useEffect, useState } from "react";

import type { ClientId } from "../../domain/types";
import type { ZohoPricingConfigDTO } from "../../data/onboardingApi";
import type { ZohoRecommendation } from "../../domain/zohoSizing";
import { fetchZohoPricing } from "../../data/onboardingApi";
import {
  docsPerYearBuckets,
  extraSeatCost,
  planById,
  planLabel,
  plansAtOrAbove,
  recommendZohoPlan,
  recommendationWhy,
} from "../../domain/zohoSizing";
import { persistAnswer, persistSizing, useCoAnswer, useCoClient } from "../../data/store";
import { dbTrue, fetchZohoRecords } from "../../data/zohoRecordsApi";
import { Callout } from "../../components/Callout";
import { DecisionCards, DecisionCardOption } from "../../components/DecisionCards";
import { QuestionCard } from "../../components/QuestionCard";
import { SubStepShell } from "../../components/SubStepShell";
import { cn } from "../../components/cn";
import { CheckIcon } from "../../components/icons";
import { useSubStepNav } from "../useSubStepNav";

const DRAFT_KEY = "zohoSizingDraft";

// What rule 2 is fed when nothing else says otherwise. One registration is
// what Zoho Books Standard covers, so it is also the answer that changes
// nothing — the safe assumption when the books have not been read.
const DEFAULT_GSTIN_COUNT = 1;

interface SizingDraft {
  docsPerYear?: string;
  userCount?: number;
  multiCurrency?: boolean;
  /** Set only once the onboarder types over the read-back. */
  gstinCount?: number;
}

const MULTI_CURRENCY_OPTIONS: DecisionCardOption[] = [
  { value: "yes", title: "Yes", description: "This client bills or pays in more than one currency." },
  { value: "no", title: "No", description: "Everything runs in rupees." },
];

const FIXED_SEAT_ROWS = [
  { title: "Your firm's access", detail: "Your team works in the books through one shared login." },
  {
    title: "Zournal system user",
    detail: "Zournal posts entries under its own user so every action is traceable in the audit log.",
  },
];

function SeatStepper({ value, onChange }: { value: number; onChange: (value: number) => void }) {
  return (
    <div className="flex shrink-0 items-center gap-2">
      <button
        type="button"
        aria-label="Decrease this client's users"
        onClick={() => onChange(Math.max(0, value - 1))}
        disabled={value <= 0}
        className="flex size-7 items-center justify-center rounded-md border text-sm hover:bg-muted/40 disabled:pointer-events-none disabled:opacity-50"
      >
        &minus;
      </button>
      <span className="w-6 text-center text-sm font-semibold tabular-nums">{value}</span>
      <button
        type="button"
        aria-label="Increase this client's users"
        onClick={() => onChange(value + 1)}
        className="flex size-7 items-center justify-center rounded-md border text-sm hover:bg-muted/40"
      >
        +
      </button>
    </div>
  );
}

export function PackageSizingStep({ clientId }: { clientId: ClientId }) {
  const client = useCoClient(clientId);
  const { goNext } = useSubStepNav(clientId, "package-sizing");
  const draft = useCoAnswer<SizingDraft>(clientId, DRAFT_KEY) ?? {};

  // Plans and thresholds come from onboarding_zoho_* so pricing changes need
  // no deploy.
  const [config, setConfig] = useState<ZohoPricingConfigDTO | undefined>();
  const [configError, setConfigError] = useState<string | undefined>();

  // How many ACTIVE GST registrations the Zoho read-back found — the ones that
  // are billable, and so the ones sizing prices on. null means we never got an
  // answer — the read has not run, or it failed — which is a different thing
  // from an organisation that genuinely has none (0).
  const [readGstins, setReadGstins] = useState<number | null>(null);

  // How many it found in total, cancelled ones included. The fetch below has
  // always had this number and always threw it away, which is why the hint
  // beside this field and the GSTIN pill in the workspace preview could show
  // different figures with nothing on screen admitting they answer different
  // questions: "how many are billable" against "how many exist in those books".
  // Both are right. Keeping the total is what lets the hint say which is which.
  const [readTotal, setReadTotal] = useState<number | null>(null);

  // The field's own text while it is being edited, so clearing it to type a
  // new number doesn't snap back to the prefill mid-keystroke.
  const [gstinInput, setGstinInput] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchZohoPricing()
      .then((loaded) => {
        if (!cancelled) setConfig(loaded);
      })
      .catch((error: Error) => {
        if (!cancelled) setConfigError(error.message);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let live = true;

    fetchZohoRecords(clientId, ["gstins"])
      .then((records) => {
        if (!live) return;
        // Same predicate as the month-close checklist: null is "we don't know",
        // and an unknown registration still counts as one.
        const all = records.gstins ?? [];
        const active = all.filter((gstin) => dbTrue(gstin.is_active) || gstin.is_active === null);
        setReadGstins(active.length);
        setReadTotal(all.length);
      })
      // No books to read, or a read that failed: the field falls back to its
      // default and says so. Not an error worth blocking sizing over.
      .catch(() => undefined);

    return () => {
      live = false;
    };
  }, [clientId]);

  if (!client) return null;

  if (configError) {
    return (
      <SubStepShell
        title="Let's size this client's Zoho plan"
        description="Couldn't load Zoho plan configuration."
        onContinue={() => void goNext()}
        continueDisabled
      >
        <p className="text-sm text-destructive">{configError}</p>
      </SubStepShell>
    );
  }

  if (!config) {
    return (
      <SubStepShell
        title="Let's size this client's Zoho plan"
        description="Loading plans…"
        onContinue={() => void goNext()}
        continueDisabled
      >
        <div className="h-24 animate-pulse rounded-lg bg-muted/40" />
      </SubStepShell>
    );
  }

  function updateDraft(next: Partial<SizingDraft>) {
    const merged = { ...draft, ...next };
    void persistAnswer<SizingDraft>(clientId, DRAFT_KEY, merged);
    void resyncSizing(merged);
  }

  /**
   * The row the rest of onboarding reads, built from one set of answers.
   *
   * A recommendation that is missing, or blocked past the ladder's top rung,
   * leaves both plan columns null: the screen has no plan to show either, and
   * a row that still named one would be a plan nobody picked.
   */
  function sizingPayload(
    answers: SizingDraft,
    recommendation: ZohoRecommendation | undefined,
    chosen: string | null
  ): Record<string, unknown> {
    const usable = recommendation && !recommendation.blockedBy ? recommendation : undefined;

    return {
      docs_per_year: answers.docsPerYear,
      user_count: answers.userCount ?? 1,
      multi_currency: answers.multiCurrency,
      gstin_count: answers.gstinCount ?? gstinPrefill,
      recommended_plan: usable ? usable.plan : null,
      chosen_plan: usable ? chosen : null,
      driving_rules: usable ? usable.drivingRules : [],
      needs_zoho_call: usable ? usable.needsZohoCall : false,
    };
  }

  /**
   * Keep the saved row honest after a plan has been chosen.
   *
   * choosePlan() used to be the only writer, so every input above it could
   * move afterwards and the row would not follow: raising the registration
   * count on a client that already had a plan showed a new recommendation on
   * screen while the row still held the old count and the old plan. So the row
   * is rewritten whenever an input changes — and it mirrors the screen exactly,
   * including having nothing to show.
   *
   * Before a plan is chosen there is nothing to keep honest: choosePlan()
   * writes the whole row on the first pick.
   *
   * A plan chosen earlier can end up BELOW the new minimum, at which point the
   * screen stops offering it — the cards only ever show the recommendation and
   * plans above it. Recording a plan that is no longer on the menu would be
   * the same lie in a different column, so it is cleared and the CA re-picks.
   */
  async function resyncSizing(answers: SizingDraft): Promise<void> {
    // `config` is loaded by the time any of this is reachable — the guard is
    // here because a hoisted function body keeps the unnarrowed type.
    if (!config || !chosenPlan) return;

    const next = recommendZohoPlan(config, {
      docsPerYear: answers.docsPerYear,
      multiCurrency: answers.multiCurrency,
      gstinCount: answers.gstinCount ?? gstinPrefill,
      totalSeats: (answers.userCount ?? 1) + reservedSeats,
    });
    // No recommendation at all means an answer the screen still needs; leave
    // the row as it stands rather than blanking it on a half-answered form.
    if (!next) return;

    const chosenRank = planById(config, chosenPlan)?.plan_rank ?? 0;
    const stillOffered = chosenRank >= (planById(config, next.plan)?.plan_rank ?? 0);

    await persistSizing(clientId, sizingPayload(answers, next, stillOffered ? chosenPlan : null));
  }

  const clientUserCount = draft.userCount ?? 1;
  const reservedSeats = config.settings.reserved_seats;
  const totalSeats = clientUserCount + reservedSeats;

  // The read-back prefills; what the onboarder typed wins over it and is what
  // gets priced and saved. They can disagree — Zoho is only as current as the
  // last read, and the onboarder may know about a registration in flight.
  const typedGstinCount = draft.gstinCount;
  const gstinPrefill = readGstins !== null && readGstins > 0 ? readGstins : DEFAULT_GSTIN_COUNT;
  const gstinCount = typedGstinCount ?? gstinPrefill;
  const gstinDisagrees = typedGstinCount !== undefined && readGstins !== null && typedGstinCount !== readGstins;

  function gstinHint(): string {
    if (readGstins === null) return "Zoho Books not read yet — enter the number of GSTINs.";
    if (readGstins === 0) return "Zoho Books reports no GST registrations — enter the number of GSTINs.";
    // Only when the two genuinely differ. A client whose registrations are all
    // active gets the sentence it has always had, to the byte — there is no
    // second number to reconcile, and "1 active of 1" would invent a
    // distinction where none exists and make nine screens noisier to fix one.
    if (readTotal !== null && readTotal !== readGstins) {
      return `${readGstins} active of ${readTotal} registrations read from Zoho Books.`;
    }

    return `${readGstins} registration${readGstins === 1 ? "" : "s"} read from Zoho Books.`;
  }

  function editGstinCount(raw: string) {
    setGstinInput(raw);
    const parsed = Number.parseInt(raw, 10);
    updateDraft({ gstinCount: raw.trim() === "" || Number.isNaN(parsed) ? undefined : Math.max(0, parsed) });
  }

  const docsOptions: DecisionCardOption[] = docsPerYearBuckets(config).map((bucket) => ({
    value: bucket.bucket_code ?? "",
    title: bucket.label ?? "",
    description: "",
    consequence: bucket.flag === "call_zoho" ? "This volume needs a conversation with Zoho - we'll flag it." : undefined,
  }));

  const recommended = recommendZohoPlan(config, {
    docsPerYear: draft.docsPerYear,
    multiCurrency: draft.multiCurrency,
    gstinCount,
    totalSeats,
  });

  // The ladder's top rung carries no plan, only the `separate_orgs` flag: past
  // it there is nothing to recommend, so the plan cards give way to the block
  // and Continue waits. Only the GSTIN rule carries the flag today, and the
  // copy says so — a flag on another rule would need its own words.
  const gstinsNeedSeparateOrgs = recommended?.blockedBy === "gstins";

  const primaryPlan = recommended ? planById(config, recommended.plan) : undefined;
  const higherPlans = recommended
    ? plansAtOrAbove(config, recommended.plan).filter((plan) => plan.id !== recommended.plan)
    : [];
  const whyText = recommended ? recommendationWhy(recommended.drivingRules) : "";
  const chosenPlan = client.zohoSizing?.chosenPlan;

  function choosePlan(planId: string) {
    if (!recommended) return;
    void persistSizing(clientId, sizingPayload(draft, recommended, planId));
  }

  /**
   * Continue is the sign-off, so the row lands on the way out as well as on
   * every change: a client sized before this field existed opens the screen
   * with the read-back's count already in the recommendation and nothing
   * touched, so there is no edit for updateDraft to catch. Awaited, so the
   * next step reads the row this screen just agreed to.
   */
  async function confirmAndContinue() {
    await resyncSizing(draft);
    await goNext();
  }

  // Extra seats are a flat monthly fee on any paid plan, so a big team never
  // forces a bigger plan (Package Picker v0.2: "Users — never changes the plan").
  const seatCost = primaryPlan ? extraSeatCost(config, primaryPlan.id, totalSeats) : undefined;

  return (
    <SubStepShell
      title="Let's size this client's Zoho plan"
      description="These answers let us recommend the smallest Zoho Books plan that fits your requirements. This is only the minimum recommended plan. You can select any higher plan if required."
      thenWhat="Continue → we'll ask about this client's Zoho account."
      onContinue={() => void confirmAndContinue()}
      continueDisabled={!chosenPlan || gstinsNeedSeparateOrgs}
    >
      <div className="flex flex-col gap-2">
        <span className="text-xs font-medium text-muted-foreground">
          Invoices, bills and expenses per year (take the higher side)
        </span>
        <DecisionCards
          options={docsOptions}
          selected={draft.docsPerYear ? [draft.docsPerYear] : []}
          onToggle={(value) => updateDraft({ docsPerYear: value })}
          multiple={false}
        />
      </div>

      <div className="flex flex-col gap-2">
        <div className="flex flex-col gap-1">
          <h3 className="text-base font-medium">How many Zoho seats does this client need?</h3>
          <p className="text-sm text-muted-foreground">
            Zoho charges per user. Three groups need access to this client's books.
          </p>
          <p className="text-sm text-muted-foreground">
            If the client needs more than one user to access Zoho Books directly, add the additional seats here.
          </p>
        </div>

        <div className="flex flex-col divide-y rounded-lg border">
          {FIXED_SEAT_ROWS.map((row) => (
            <div key={row.title} className="flex items-center justify-between gap-4 p-4">
              <div className="flex flex-col gap-0.5">
                <span className="text-sm font-medium">{row.title}</span>
                <span className="text-xs text-muted-foreground">{row.detail}</span>
              </div>
              <span className="text-sm font-semibold tabular-nums">1</span>
            </div>
          ))}

          <div className="flex items-center justify-between gap-4 p-4">
            <div className="flex flex-col gap-0.5">
              <span className="text-sm font-medium">This client's users</span>
              <span className="text-xs text-muted-foreground">
                People at the client who log into Zoho directly. Set to 0 if nobody at the client uses Zoho.
              </span>
            </div>
            <SeatStepper value={clientUserCount} onChange={(value) => updateDraft({ userCount: value })} />
          </div>

          <div className="flex items-center justify-between gap-4 bg-muted/30 p-4">
            <span className="text-sm font-medium">Total Zoho seats</span>
            <span className="text-base font-semibold tabular-nums">{totalSeats}</span>
          </div>
        </div>
      </div>

      <QuestionCard
        headline="Does this client need multi-currency?"
        whyLine="Multi-currency support only exists on higher Zoho plans."
        options={MULTI_CURRENCY_OPTIONS}
        selected={draft.multiCurrency === undefined ? undefined : draft.multiCurrency ? "yes" : "no"}
        onSelect={(value) => updateDraft({ multiCurrency: value === "yes" })}
      />

      <div className="flex flex-col gap-2">
        <div className="flex flex-col gap-1">
          <h3 className="text-base font-medium">How many GST registrations does this client have?</h3>
          <p className="text-sm text-muted-foreground">
            One Zoho Books organisation covers one GSTIN on the lower plans. A client registered in more than one
            state needs a plan that carries them, or separate organisations.
          </p>
        </div>

        <label className="flex max-w-xs flex-col gap-1 text-sm">
          <span className="sr-only">Number of GST registrations</span>
          <input
            type="number"
            min={0}
            inputMode="numeric"
            value={gstinInput ?? String(gstinCount)}
            onChange={(event) => editGstinCount(event.target.value)}
            onBlur={() => setGstinInput(null)}
            className="h-9 rounded-md border bg-background px-3 text-sm"
          />
        </label>

        <p className="text-xs text-muted-foreground">{gstinHint()}</p>
        {/* "read N registrations" named the ACTIVE count but sounded like the
            total, the same conflation as the hint above, and once that hint
            distinguishes the two this would have contradicted it outright. The
            active count is what the entered number is compared against, so it
            is the one to name. */}
        {gstinDisagrees && (
          <Callout variant="warning">
            Zoho Books has {readGstins} active registration{readGstins === 1 ? "" : "s"}, and you
            entered {typedGstinCount}. Sizing uses your number.
          </Callout>
        )}
      </div>

      {gstinsNeedSeparateOrgs && (
        <Callout variant="warning">
          <p className="font-medium text-foreground">This many registrations needs separate Zoho organisations</p>
          <p className="mt-1">
            {gstinCount} GST registrations don't fit one Zoho Books organisation, and no plan on the ladder covers
            them. The client needs more than one Zoho organisation — a decision to take before a plan is picked, not
            a bigger plan. If that number isn't right, correct it above and the recommendation comes back.
          </p>
        </Callout>
      )}

      {recommended && primaryPlan && !gstinsNeedSeparateOrgs && (
        <div className="flex flex-col gap-4">
          <button
            type="button"
            onClick={() => choosePlan(primaryPlan.id)}
            className={cn(
              "flex flex-col gap-2 rounded-lg border-2 p-5 text-left transition-colors",
              chosenPlan === primaryPlan.id ? "border-primary bg-primary/5" : "border-primary/40 hover:bg-muted/40"
            )}
          >
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs font-medium uppercase tracking-wide text-primary">Minimum Recommended Plan</span>
              {chosenPlan === primaryPlan.id && <CheckIcon className="size-4 text-primary" />}
            </div>
            <h3 className="text-lg font-semibold">{primaryPlan.label}</h3>
            <p className="text-2xl font-semibold">
              ₹{primaryPlan.price_per_month.toLocaleString("en-IN")}
              <span className="text-sm font-normal text-muted-foreground"> /month</span>
            </p>
            <p className="text-sm text-muted-foreground">
              {primaryPlan.users_included} users included · billed annually, excl. GST
            </p>
            {seatCost && seatCost.extraSeats > 0 ? (
              <p className="text-sm text-muted-foreground">
                This client needs {totalSeats} seats, so {seatCost.extraSeats} extra{" "}
                {seatCost.extraSeats === 1 ? "seat" : "seats"} at ₹
                {config.settings.extra_seat_price.toLocaleString("en-IN")}/month each - ₹
                {seatCost.monthly.toLocaleString("en-IN")}/month on top. Extra seats never require a bigger plan.
              </p>
            ) : (
              <p className="text-sm text-muted-foreground">
                Covers the {totalSeats} Zoho seats this client needs, from the total above.
              </p>
            )}
            <p className="text-sm text-muted-foreground">
              This is the minimum plan that meets the client's requirements. You are free to select this plan or any
              higher Zoho Books plan.
            </p>
            {whyText && <p className="text-sm">{whyText}</p>}
            {recommended.needsZohoCall && (
              <p className="text-sm text-muted-foreground">
                Flagged: this document volume needs a conversation with Zoho.
              </p>
            )}
          </button>

          {higherPlans.length > 0 && (
            <div className="flex flex-col gap-2">
              <p className="text-xs text-muted-foreground">You can pick this or any plan above it.</p>
              <div className="flex flex-col gap-2">
                {higherPlans.map((plan) => (
                  <button
                    key={plan.id}
                    type="button"
                    onClick={() => choosePlan(plan.id)}
                    className={cn(
                      "flex items-center justify-between gap-2 rounded-md border p-3 text-left text-sm transition-colors",
                      chosenPlan === plan.id ? "border-primary bg-primary/5" : "border-border hover:bg-muted/40"
                    )}
                  >
                    <span className="font-medium">{planLabel(config, plan.id)}</span>
                    <span className="text-muted-foreground">
                      ₹{plan.price_per_month.toLocaleString("en-IN")}/month · {plan.users_included} users
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <p className="text-xs text-muted-foreground/70">Prices from {config.settings.pricing_source}.</p>
        </div>
      )}
    </SubStepShell>
  );
}
